import { Application, PlatformEnvironment, Team } from '@mews/enums';
import {
    AppClockService,
    CreateAzureAppInsightsLoggerAdapter,
    createAzureAppInsightsLoggerAdapter,
    createConsoleLoggerAdapter,
    createNewRelicLoggerAdapter,
    DateTimeUtcService,
    getAzureErrorsAsString,
    isNewRelicLoggerActive,
    NullableNewRelicLoggerConfig,
    SentryReporterService,
} from '@mews/framework';

export interface InitLoggerAdaptersParams extends Pick<CreateAzureAppInsightsLoggerAdapter, 'context'> {
    isProduction: boolean;
    appClockService: AppClockService;
    applicationInsightsInstrumentationKey:
        | CreateAzureAppInsightsLoggerAdapter['applicationInsightsInstrumentationKey']
        | null;
    appVersion: string;
    sentryReporterService?: SentryReporterService;
    environment: PlatformEnvironment;
    newRelicLoggerConfig: NullableNewRelicLoggerConfig;
}

const initLoggerAdapters = ({
    context,
    newRelicLoggerConfig,
    applicationInsightsInstrumentationKey,
    isProduction,
    appClockService,
    appVersion,
    sentryReporterService,
    environment,
}: InitLoggerAdaptersParams) => {
    const loggers = [];

    if (applicationInsightsInstrumentationKey != null) {
        const azureAppInsightsAdapter = createAzureAppInsightsLoggerAdapter({
            context,
            applicationInsightsInstrumentationKey,
            getNowUTC: () => DateTimeUtcService.toISOString(appClockService.getAppNowUtc()),
            onError: (error) =>
                sentryReporterService != null &&
                sentryReporterService.logWarning({
                    error,
                    extra: { origin: 'AzureAppInsightsLoggerAdapter', errors: getAzureErrorsAsString(error) },
                }),
        });

        loggers.push(azureAppInsightsAdapter);
    }

    if (isNewRelicLoggerActive(newRelicLoggerConfig)) {
        const newRelicLoggerAdapter = createNewRelicLoggerAdapter({
            apiEndpoint: newRelicLoggerConfig.logEndpoint,
            apiInsertKey: newRelicLoggerConfig.insertKey,
            getTimestamp: appClockService.getValue,
            commonAttributes: {
                team: Team.Accounts,
                environment,
                app: {
                    name: Application.Commander,
                    version: appVersion,
                },
            },
            sentryReporterService,
        });

        loggers.push(newRelicLoggerAdapter);
    }

    if (!isProduction) {
        loggers.push(createConsoleLoggerAdapter());
    }

    return loggers;
};

export default initLoggerAdapters;
